import { useCallback, useEffect, useState } from 'react';
import { fetchCampaigns, useApiResource } from '../lib/api';
import { useCompany } from '../lib/companyContext';
import { usePeriod } from '../lib/periodContext';
import { describeSelection } from '../lib/period';
import { AudioPlayer } from '../components/AudioPlayer';
import { AudioPlayerProvider } from '../components/AudioPlayerProvider';
import { StatusPill } from '../components/LeadsTable';
import { Pagination } from '../components/Pagination';
import { EmptyState, ErrorPanel, PageHeader, RefreshingDot, SimulatedNotice } from '../components/PageState';
import {
  campaignStatusTone,
  formatBRLFromCents,
  formatCount,
  formatDateTime,
  formatDuration,
  maskPhoneDisplay,
  shortenName,
  CAMPAIGN_STATUS_LABEL,
  CHANNEL_LABEL,
  TIER_LABEL,
} from '../lib/format';
import type { CampaignListItem } from '../types';

const PAGE_SIZE = 20;

/**
 * Historico de ligacoes da IA e mensagens de WhatsApp (RF06..RF10 / RNF17).
 *
 * O provedor de audio envolve esta pagina, e nao a aplicacao inteira, porque
 * esta e a unica tela com gravacao e so uma rota fica montada por vez.
 *
 * A lista e de cartoes, e nao uma tabela: cada registro carrega um player, e
 * player dentro de celula de tabela vira uma linha alta demais.
 *
 * Sem atualizacao periodica de proposito: uma troca de lista embaixo de uma
 * gravacao em andamento interromperia o audio no meio da escuta.
 *
 * A busca depende da EMPRESA EM FOCO (`company.id`), nao so do periodo: a
 * equipe (`platform_admin`) troca de empresa pelo seletor e o `X-Company-Id`
 * muda sem a rota remontar. Sem `company.id` nas dependencias a tela ficava
 * com os acionamentos da empresa anterior sob o nome da nova (revisao de
 * 25/09/2026) -- dado de um lojista rotulado como de outro.
 */
export function Campanhas(): JSX.Element {
  const { company } = useCompany();
  const { range, selection } = usePeriod();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    document.title = `Campanhas — ${company.name} · Converte Fácil`;
  }, [company.name]);

  useEffect(() => {
    setOffset(0);
  }, [range, company.id]);

  const buscar = useCallback(
    (signal: AbortSignal) => fetchCampaigns(range, { limit: PAGE_SIZE, offset }, signal),
    // company.id entra de proposito: o cabecalho X-Company-Id muda com ele.
    [range, offset, company.id],
  );
  const { state, reload } = useApiResource(buscar);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <PageHeader
        title="Campanhas"
        description={
          <>
            Ligações da IA e mensagens de WhatsApp em {describeSelection(selection).toLowerCase()}, com desfecho,
            duração e gravação.
          </>
        }
        aside={<RefreshingDot active={state.status === 'success' && state.refreshing} />}
      />

      <div data-slot="historico-de-campanhas" className="flex flex-col gap-4">
        {state.status === 'loading' ? <HistoricoCarregando /> : null}

        {state.status === 'error' ? (
          <ErrorPanel title="Não foi possível carregar o histórico" error={state.error} onRetry={reload} />
        ) : null}

        {state.status === 'success' ? (
          <>
            {state.simulated ? (
              <SimulatedNotice>
                As gravações são trilhas de silêncio geradas no próprio navegador, só para exercitar o player.
                Nenhuma ligação real foi feita.
              </SimulatedNotice>
            ) : null}

            {state.data.items.length === 0 ? (
              <EmptyState
                title="Nenhum acionamento no período"
                description="As ligações da IA e as mensagens de WhatsApp aparecem aqui assim que a fila de 30 minutos vencer o primeiro carrinho abandonado."
              />
            ) : (
              <AudioPlayerProvider>
                <p className="text-sm text-ink-subtle">
                  {formatCount(state.data.total)} {state.data.total === 1 ? 'acionamento' : 'acionamentos'} no período.
                </p>
                <ul className="flex flex-col gap-3">
                  {state.data.items.map((campanha) => (
                    <CampanhaCard key={campanha.id} campanha={campanha} />
                  ))}
                </ul>
                <Pagination total={state.data.total} limit={state.data.limit} offset={state.data.offset} onChange={setOffset} />
              </AudioPlayerProvider>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}

function CampanhaCard({ campanha }: { readonly campanha: CampaignListItem }): JSX.Element {
  const nome = shortenName(campanha.leadName);
  const voz = campanha.channel === 'voice';

  return (
    <li className="flex flex-col gap-3 rounded-2xl border border-line bg-surface px-4 py-4 shadow-soft sm:px-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${voz ? 'bg-series-1/10 text-series-1' : 'bg-series-3/10 text-series-3'}`}>
            {voz ? <PhoneGlyph /> : <ChatGlyph />}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">{nome}</p>
            <p className="truncate text-xs text-ink-subtle">
              {maskPhoneDisplay(campanha.phoneE164)} · {CHANNEL_LABEL[campanha.channel]} · {TIER_LABEL[campanha.tier]}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <StatusPill tone={campaignStatusTone(campanha.status)} label={CAMPAIGN_STATUS_LABEL[campanha.status]} />
          <span className="tabular-nums text-xs text-ink-subtle">{formatDateTime(campanha.createdAt)}</span>
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <div>
          <dt className="text-xs text-ink-subtle">Valor em disputa</dt>
          <dd className="tabular-nums text-ink">{formatBRLFromCents(campanha.amountCents)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-subtle">Duração</dt>
          <dd className="tabular-nums text-ink">{formatDuration(campanha.durationSeconds)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-subtle">Resultado</dt>
          <dd className="flex items-center gap-1.5 text-ink">
            {campanha.converted ? (
              <>
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-status-good" />
                Carrinho recuperado
              </>
            ) : (
              'Sem conversão'
            )}
          </dd>
        </div>
      </dl>

      {campanha.errorMessage === null ? null : (
        <p className="flex items-start gap-2 rounded-lg bg-ink/5 px-3 py-2 text-xs text-ink-muted">
          <span aria-hidden="true" className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-status-critical" />
          {campanha.errorMessage}
        </p>
      )}

      {campanha.recordingUrl === null ? null : (
        <div className="border-t border-line pt-3">
          <AudioPlayer
            id={campanha.id}
            src={campanha.recordingUrl}
            label={`gravação da ligação com ${nome}`}
            fallbackDurationSeconds={campanha.durationSeconds}
          />
        </div>
      )}
    </li>
  );
}

function HistoricoCarregando(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <p role="status" className="sr-only">Carregando o histórico de campanhas</p>
      {[0, 1, 2].map((linha) => (
        <div key={linha} aria-hidden="true" className="h-36 animate-pulse rounded-2xl border border-line bg-surface" />
      ))}
    </div>
  );
}

const glyph = { viewBox: '0 0 24 24', className: 'h-5 w-5', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true, focusable: 'false' as const };

function PhoneGlyph(): JSX.Element {
  return <svg {...glyph}><path d="M5.5 4.5h3l1.5 4-2 1.5a9 9 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2 2A15 15 0 0 1 3.5 6.5a2 2 0 0 1 2-2Z" /></svg>;
}
function ChatGlyph(): JSX.Element {
  return <svg {...glyph}><path d="M4.5 6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H10l-4.5 3.5V16.5h-1a2 2 0 0 1 0-4v-6Z" /></svg>;
}
